import React, { useCallback, useEffect, useState } from 'react';
import {
  Drawer, Descriptions, Spin, Alert, Button, Space, Divider, Tag, Modal, Input, Collapse,
} from 'antd';
import {
  CheckCircleOutlined, EditOutlined, UserOutlined, EnvironmentOutlined,
  PaperClipOutlined, ExclamationCircleOutlined, StopOutlined, WarningOutlined,
  CommentOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import i18n from '../../../../i18n';
import mobileService from '../../../../services/mobileService';
import { formatDate, can, HAB, TYPES_DOMMAGE } from '../../../../variables/constants';
import CarteGps from './CarteGps';
import PiecesMobiles from './PiecesMobiles';
import BadgeCanal from './BadgeCanal';
import FilDiscussionMobile from './FilDiscussionMobile';

/**
 * Examen d'une declaration mobile avant decision.
 *
 * Un tiroir et non une page : l'agent enchaine les declarations d'une file, revenir
 * a la liste entre chacune casserait le rythme de traitement.
 *
 * Deux issues, toutes deux servies par le backend :
 *   - VALIDER  → SOUMISE devient NOUVEAU, le dossier entre dans le flux metier et
 *                les delais CEDEAO commencent a courir. Observation facultative.
 *   - CLASSER  → SOUMISE devient SANS_SUITE. Motif OBLIGATOIRE : un dossier ecarte
 *                sans raison ecrite est indefendable devant la victime.
 *
 * La validation ne COMPLETE pas le dossier : la declaration mobile est simplifiee,
 * le dossier cree est partiel. D'ou l'enchainement propose vers le formulaire de
 * modification.
 */

const { TextArea } = Input;

const Vide = () => <span style={{ color: '#9ca3af' }}>-</span>;
const val = (v) => (v || <Vide />);

const libelleDommage = (v) => TYPES_DOMMAGE.find((o) => o.value === v)?.label || v || '-';
const nomComplet = (prenom, nom) => [prenom, nom].filter(Boolean).join(' ');

export default function DeclarationMobileDrawer({ open, sinistreTrackingId, onClose, onDecision }) {
  const { t } = useTranslation('dossierSinistre');
  const navigate = useNavigate();
  const [declaration, setDeclaration] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [rejetOuvert, setRejetOuvert] = useState(false);
  const [motif, setMotif] = useState('');

  const peutTrancher = can(HAB.SINISTRE_VALIDER_DECLARATION);

  const charger = useCallback(async () => {
    if (!sinistreTrackingId) return;
    setChargement(true);
    setErreur(null);
    try {
      setDeclaration(await mobileService.detailDeclaration(sinistreTrackingId));
    } catch (err) {
      setErreur(err?.response?.data?.message || i18n.t('dossierSinistre:tiroirDeclaration.introuvable'));
      setDeclaration(null);
    } finally {
      setChargement(false);
    }
  }, [sinistreTrackingId]);

  useEffect(() => {
    if (open) charger();
    else { setDeclaration(null); setErreur(null); setRejetOuvert(false); setMotif(''); }
  }, [open, charger]);

  const valider = (puisCompleter) => {
    Modal.confirm({
      title: t('tiroirDeclaration.validerTitre'),
      icon: <ExclamationCircleOutlined style={{ color: '#1a6b3c' }} />,
      content: (
        <div style={{ fontSize: 13 }}>
          <p>
            <Trans
              t={t}
              i18nKey="tiroirDeclaration.validerContenu"
              values={{ numero: declaration?.numeroSinistreLocal || '-' }}
              components={{ strong: <strong /> }}
            />
          </p>
          <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 0 }}>
            {t('tiroirDeclaration.validerNote')}
          </p>
        </div>
      ),
      okText: puisCompleter ? t('tiroirDeclaration.validerEtCompleter') : t('tiroirDeclaration.valider'),
      cancelText: t('tiroirDeclaration.annuler'),
      okButtonProps: { style: { background: '#1a6b3c', borderColor: '#1a6b3c' } },
      onOk: async () => {
        setEnCours(true);
        try {
          await mobileService.validerDeclaration(sinistreTrackingId);
          toast.success(t('tiroirDeclaration.validee'));
          onDecision?.(sinistreTrackingId);
          if (puisCompleter) navigate(`/sinistres/${sinistreTrackingId}/modifier`);
        } catch (err) {
          toast.error(err?.response?.data?.message || t('tiroirDeclaration.validationImpossible'));
        } finally {
          setEnCours(false);
        }
      },
    });
  };

  const classerSansSuite = async () => {
    // Le backend refuse un motif vide ; on l'arrete ici pour eviter l'aller-retour.
    if (!motif.trim()) {
      toast.warning(t('tiroirDeclaration.motifObligatoire'));
      return;
    }
    setEnCours(true);
    try {
      await mobileService.rejeterDeclaration(sinistreTrackingId, motif.trim());
      toast.success(t('tiroirDeclaration.classee'));
      setRejetOuvert(false);
      setMotif('');
      onDecision?.(sinistreTrackingId);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('tiroirDeclaration.classementImpossible'));
    } finally {
      setEnCours(false);
    }
  };

  const declarant = declaration ? nomComplet(declaration.declarantPrenom, declaration.declarantNom) : '';
  const usager = declaration ? nomComplet(declaration.usagerPrenom, declaration.usagerNom) : '';
  const ecartIdentite = !!declarant && !!usager
    && declarant.trim().toLowerCase() !== usager.trim().toLowerCase();

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
      title={
        <Space size={8} wrap>
          <span style={{ fontWeight: 700 }}>
            {declaration?.numeroSinistreLocal || t('tiroirDeclaration.titre')}
          </span>
          <BadgeCanal canal="MOBILE" size="sm" />
        </Space>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>{t('tiroirDeclaration.fermer')}</Button>
          <Button
            danger
            icon={<StopOutlined />}
            disabled={!peutTrancher || !declaration || enCours}
            onClick={() => setRejetOuvert(true)}
          >
            {t('tiroirDeclaration.classerSansSuite')}
          </Button>
          <Button
            icon={<CheckCircleOutlined />}
            disabled={!peutTrancher || !declaration || enCours}
            onClick={() => valider(false)}
          >
            {t('tiroirDeclaration.validerSeulement')}
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            disabled={!peutTrancher || !declaration || enCours}
            loading={enCours}
            onClick={() => valider(true)}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
          >
            {t('tiroirDeclaration.validerEtCompleter')}
          </Button>
        </div>
      }
    >
      {chargement && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}

      {erreur && !chargement && <Alert type="error" showIcon message={erreur} />}

      {declaration && !chargement && (
        <>
          {!peutTrancher && (
            <Alert
              type="info"
              showIcon
              message={t('tiroirDeclaration.consultationSeule')}
              style={{ marginBottom: 16 }}
            />
          )}

          <Descriptions column={2} size="small" bordered labelStyle={{ width: 150, fontSize: 12 }}>
            <Descriptions.Item label={t('tiroirDeclaration.recueLe')}>{val(declaration.dateDeclaration && formatDate(declaration.dateDeclaration))}</Descriptions.Item>
            <Descriptions.Item label={t('tiroirDeclaration.accidentDu')}>{val(declaration.dateAccident && formatDate(declaration.dateAccident))}</Descriptions.Item>
            <Descriptions.Item label={t('tiroirDeclaration.dommages')}>{libelleDommage(declaration.typeDommage)}</Descriptions.Item>
            <Descriptions.Item label={t('tiroirDeclaration.piecesJointes')}>
              {/* null = comptage non fait. Afficher « 0 » orienterait a tort vers un rejet. */}
              {declaration.nombreDocuments == null
                ? <span style={{ color: '#9ca3af' }}>{t('tiroirDeclaration.nonComptees')}</span>
                : declaration.nombreDocuments}
            </Descriptions.Item>
            <Descriptions.Item label={t('tiroirDeclaration.lieu')} span={2}>{val(declaration.lieuAccident)}</Descriptions.Item>
            <Descriptions.Item label={t('tiroirDeclaration.communeVille')} span={2}>
              {val([declaration.commune, declaration.ville].filter(Boolean).join(', '))}
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left" style={{ fontSize: 12.5 }}>
            <UserOutlined /> {t('declarationMobile.identites')}
          </Divider>

          {ecartIdentite && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={t('tiroirDeclaration.declarantPasTitulaire')}
              description={t('tiroirDeclaration.declarationPourTiers')}
              style={{ marginBottom: 12 }}
            />
          )}

          <Descriptions column={2} size="small" bordered labelStyle={{ width: 150, fontSize: 12 }}>
            <Descriptions.Item label={t('declarationMobile.declarantSaisi')}>
              {val(declarant)}
              {declaration.declarantQualite && (
                <Tag style={{ marginLeft: 8, borderRadius: 12 }}>{declaration.declarantQualite}</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('declarationMobile.telephoneDeclarant')}>{val(declaration.declarantTelephone)}</Descriptions.Item>
            <Descriptions.Item label={t('declarationMobile.compteMobile')}>{val(usager)}</Descriptions.Item>
            <Descriptions.Item label={t('declarationMobile.telephoneCompte')}>{val(declaration.usagerTelephone)}</Descriptions.Item>
          </Descriptions>
          <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 6 }}>
            {t('declarationMobile.noteDeclarant')}
          </div>

          <Divider orientation="left" style={{ fontSize: 12.5 }}>
            <EnvironmentOutlined /> {t('tiroirDeclaration.positionGps')}
          </Divider>
          <CarteGps latitude={declaration.latitude} longitude={declaration.longitude} />

          <Divider orientation="left" style={{ fontSize: 12.5 }}>
            <PaperClipOutlined /> {t('tiroirDeclaration.piecesDossier')}
          </Divider>
          <PiecesMobiles sinistreTrackingId={sinistreTrackingId} titre={t('tiroirDeclaration.piecesTransmises')} />

          {/*
            Replie par defaut, et c'est le point : monter le fil chargerait ses messages,
            or la lecture les marque comme lus cote serveur. Une file de declarations
            parcourue au clavier eteindrait ainsi le signal de non-lu de dizaines de
            dossiers que personne n'a ouverts. L'agent deplie quand il veut ecrire.

            Sa place ici est celle du cas reel : une declaration incomplete se reclame
            avant d'etre validee ou classee, pas apres.
          */}
          <Divider orientation="left" style={{ fontSize: 12.5 }}>
            <CommentOutlined /> {t('tiroirDeclaration.echangesUsager')}
          </Divider>
          <Collapse
            ghost
            items={[{
              key: 'fil',
              label: (
                <span style={{ fontSize: 12.5, fontWeight: 650 }}>
                  {t('tiroirDeclaration.ouvrirFil')}
                  <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
                    {t('tiroirDeclaration.ouvrirFilDetail')}
                  </span>
                </span>
              ),
              children: (
                <FilDiscussionMobile
                  sinistreTrackingId={sinistreTrackingId}
                  numeroSinistre={declaration.numeroSinistreLocal}
                  hauteur={300}
                />
              ),
            }]}
          />

          <Modal
            open={rejetOuvert}
            title={t('tiroirDeclaration.rejetTitre')}
            okText={t('tiroirDeclaration.classerSansSuite')}
            cancelText={t('tiroirDeclaration.annuler')}
            okButtonProps={{ danger: true, loading: enCours, disabled: !motif.trim() }}
            onOk={classerSansSuite}
            onCancel={() => { setRejetOuvert(false); setMotif(''); }}
            destroyOnClose
          >
            <p style={{ fontSize: 13 }}>
              <Trans
                t={t}
                i18nKey="tiroirDeclaration.rejetContenu"
                values={{ numero: declaration.numeroSinistreLocal || '-' }}
                components={{ strong: <strong /> }}
              />
            </p>
            <TextArea
              rows={4}
              maxLength={1000}
              showCount
              placeholder={t('tiroirDeclaration.motifPlaceholder')}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          </Modal>
        </>
      )}
    </Drawer>
  );
}
